import { Megaphone } from "lucide-react";
import { useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import { type NavItem, Shell } from "./components/Shell.tsx";
import { api } from "./lib/api.ts";
import {
	beginLogin,
	getToken,
	logout,
	noteSilentAttempt,
	shouldTrySilentLogin,
} from "./lib/auth.ts";
import { type Role, roleAtLeast } from "./lib/roles.ts";
import { useLoad } from "./lib/useLoad.ts";
import { Access } from "./pages/Access.tsx";
import { Accounts } from "./pages/Accounts.tsx";
import { Agents } from "./pages/Agents.tsx";
import { Audit } from "./pages/Audit.tsx";
import { Callback } from "./pages/Callback.tsx";
import { Overview } from "./pages/Overview.tsx";
import { Proxies } from "./pages/Proxies.tsx";
import { Register } from "./pages/Register.tsx";
import { SignIn } from "./pages/SignIn.tsx";
import { System } from "./pages/System.tsx";
import { Team } from "./pages/Team.tsx";
import { Teams } from "./pages/Teams.tsx";

/** The working set every signed-in user gets, subject to what they hold. */
const WORKING: NavItem[] = [
	{ label: "Overview", path: "/", group: "Buying" },
	{ label: "Ad accounts", path: "/accounts", group: "Buying" },
	{ label: "Agents", path: "/agents", group: "Buying" },
	{ label: "Proxies", path: "/proxies", group: "Buying" },
];

/** A tenant's own people. Our operators have no team, so they never see it. */
const TEAM: NavItem[] = [{ label: "My team", path: "/team", group: "Buying" }];

/**
 * Governing screens — ours alone; tenants never see these links.
 *
 * Each carries the rung its endpoints actually require, because the group as a
 * whole does not have one. Offering all of them to every seated operator meant a
 * `manager` was shown Teams, Access and Trail and got 403 from all three — a
 * menu that offers what it cannot deliver, which reads as a broken console
 * rather than as a permission.
 *
 * The floors are read off the routes rather than assumed. `GET /api/v1/orgs`
 * gates on `isPlatformAdmin`; everything under `/api/v1/access`, including
 * `/audit`, gates on `atLeast(role, "admin")`; `GET /api/v1/system/schema` gates
 * on `manager`. So System is the one screen a manager may genuinely open, and a
 * blanket admin rule would have hidden the only one that worked.
 *
 * `minRole` is required rather than optional on purpose. A new governing screen
 * cannot be added without stating its rung — which is the part that would
 * otherwise drift back, because this asymmetry arrived exactly that way: somebody
 * added a link and nobody added a gate.
 *
 * This hides links. It grants nothing: every one of these routes is refused
 * server-side against fbads' own tables, whatever the menu shows.
 */
const GOVERN: (NavItem & { minRole: Role })[] = [
	{ label: "Teams", path: "/teams", group: "Operate", minRole: "admin" },
	{ label: "Access", path: "/access", group: "Operate", minRole: "admin" },
	{ label: "System", path: "/system", group: "Operate", minRole: "manager" },
	{ label: "Trail", path: "/audit", group: "Operate", minRole: "admin" },
];

export function App() {
	return (
		<Routes>
			<Route path="/auth/callback" element={<Callback />} />
			<Route path="*" element={getToken() ? <Console /> : <Entry />} />
		</Routes>
	);
}

/**
 * No token here yet. If the operator still holds a g1-auth session, the
 * authorize round trip returns immediately and they never see this screen —
 * which is the point: being signed in to G1 should mean being signed in here.
 * The manual screen is the fallback for a genuine sign-out or a failed attempt.
 */
function Entry() {
	const silent = shouldTrySilentLogin();

	useEffect(() => {
		if (!silent) return;
		noteSilentAttempt();
		void beginLogin(window.location.pathname + window.location.search);
	}, [silent]);

	if (!silent) return <SignIn />;
	return (
		<main className="grid min-h-dvh place-items-center p-6">
			<p className="text-sm text-muted">Signing you in…</p>
		</main>
	);
}

/**
 * Standing decides the whole shape of the app: our operators get the platform
 * console, an approved team gets the tenant console, and anyone else gets the
 * registration flow.
 */
function Console() {
	const [standing, reload] = useLoad(() => api.standing());

	if (standing.state === "loading") {
		return (
			<main className="grid min-h-dvh place-items-center p-6">
				<p className="text-sm text-muted">Loading…</p>
			</main>
		);
	}

	if (standing.state === "failed") {
		return (
			<main className="grid min-h-dvh place-items-center p-6">
				<div className="max-w-md text-center">
					<h1 className="text-2xl font-bold text-content">The console is unreachable.</h1>
					<p className="mt-2 font-mono text-sm text-muted">{standing.why}</p>
				</div>
			</main>
		);
	}

	const me = standing.data;
	if (me.standing === "unaffiliated" || me.standing === "pending") {
		return <Register standing={me} onRegistered={reload} />;
	}

	const isPlatform = me.standing === "platform";
	// "user" is the safe floor: a seated operator with no role recorded is offered
	// nothing governing, rather than everything.
	const platformRole: Role = me.platform?.role ?? "user";

	return (
		<Shell
			title={me.team?.name ?? "G1 FB Ads"}
			Icon={Megaphone}
			nav={
				isPlatform
					? [...WORKING, ...GOVERN.filter((item) => roleAtLeast(platformRole, item.minRole))]
					: [...WORKING, ...TEAM]
			}
			// The role shown in the top bar is the one that governs this console:
			// a tenant's rung in their own team, or our operator seat.
			user={{
				email: me.email,
				role: isPlatform ? (me.platform?.role ?? "user") : (me.team?.role ?? "member"),
			}}
			onSignOut={() => void logout()}
		>
			<Routes>
				<Route path="/" element={<Overview />} />
				<Route path="/accounts" element={<Accounts />} />
				<Route path="/agents" element={<Agents />} />
				<Route path="/proxies" element={<Proxies />} />
				{!isPlatform && <Route path="/team" element={<Team standing={me} />} />}
				{isPlatform && <Route path="/teams" element={<Teams />} />}
				{isPlatform && <Route path="/access" element={<Access />} />}
				{isPlatform && <Route path="/system" element={<System />} />}
				{isPlatform && <Route path="/audit" element={<Audit />} />}
				<Route path="*" element={<Overview />} />
			</Routes>
		</Shell>
	);
}
